import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";

import { notifyListRefresh } from "../../../utils/listNavigation";
import { callUploadFile } from "../../../api/settings/service";
import {
  getLifeContract,
  reqClameLifeContract,
} from "../../../api/contract/service";
import {
  decodeBase64,
  causeDeadList,
  methodReceiveMoneyList,
  bankList,
} from "../../../utils/commonUtil";

import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import FileUploadGrid from "../../../components/upload-file";
import SwalAlert from "../../../components/common/alert";

import "../../../components/i18n/lang";
import "react-datepicker/dist/react-datepicker.css";
import "./clame.css";

const LifeClaim = () => {
  const userInfo = useSelector((state) => state.dataLogin);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const [contractDetail, setContractDetail] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [request, setRequest] = useState({
    userId: userInfo?.data?.user?.userId,
    contractNumber: "",
    deadDate: "",
    claimCause: "",
    methodReceiveMoney: "",
    claimTypeId: 1,
    carriredCode: userInfo?.data?.user?.userId,
    carriredName:
      userInfo?.data?.user?.firstName +
      " " +
      (userInfo?.data?.user?.lastName || ""),
    requestOs: "web",
    documentPath: "",
    documentFile: null,
    beneficiaryDocumentPath: "",
    beneficiaryDocumentFile: null,
    description: "",
    heir: "",
    bankType: "",
    bankAccName: "",
    bankAcc: "",
  });

  useEffect(() => {
    if (!params?.data) return;

    const contractNumber = decodeBase64(params.data);
    if (!contractNumber) return;

    setRequest((prev) => ({
      ...prev,
      contractNumber,
      carriredCode: userInfo?.data?.user?.userId,
      carriredName:
        userInfo?.data?.user?.firstName +
        " " +
        (userInfo?.data?.user?.lastName || ""),
      userId: userInfo?.data?.user?.userId,
    }));

    loadContractDetail(contractNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.data]);

  const loadContractDetail = async (contractNumber) => {
    try {
      setLoadingContract(true);
      const rsp = await dispatch(getLifeContract({ contractNumber }));
      if (rsp?.payload?.responseCode === "00000") {
        setContractDetail(rsp?.payload?.data || null);
      }
    } catch (error) {
      console.error("Load life contract for claim error:", error);
    } finally {
      setLoadingContract(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    SwalAlert.confirm(t("nameSave"), t("confirmSave"), async () => {
      try {
        setSubmitting(true);
        let documentPath = "";
        let beneficiaryDocumentPath = "";
        let error = null;

        if (request.documentFile !== null) {
          const upload = await dispatch(
            callUploadFile(request.documentFile.file)
          );
          if (upload?.payload?.responseCode === "00000") {
            documentPath = JSON.stringify(upload?.payload?.data);
          } else {
            error = t("uploadFile.clameDocumentFile");
          }
        }

        if (request.beneficiaryDocumentFile !== null) {
          const upload = await dispatch(
            callUploadFile(request.beneficiaryDocumentFile.file)
          );
          if (upload?.payload?.responseCode === "00000") {
            beneficiaryDocumentPath = JSON.stringify(upload?.payload?.data);
          } else {
            error = t("uploadFile.clameBeneficiaryDocumentFile");
          }
        }

        if (error !== null) {
          SwalAlert.error(t("claimPages.common.errorTitle"), error);
          return;
        }

        const updatedRequest = {
          ...request,
          documentPath,
          beneficiaryDocumentPath,
        };

        const rsp = await dispatch(reqClameLifeContract(updatedRequest));
        if (rsp?.payload?.responseCode === "00000") {
          notifyListRefresh();
          navigate("/admin/contractControl/lifeInsurance");
        } else {
          SwalAlert.error(
            t("claimPages.common.errorTitle"),
            rsp?.payload?.responseCode +
              " - " +
              rsp?.payload?.responseDescription
          );
        }
      } catch (error) {
        SwalAlert.error(
          t("claimPages.common.errorTitle"),
          t("claimPages.common.serverError")
        );
      } finally {
        setSubmitting(false);
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRequest((prev) => ({ ...prev, [name]: value }));
  };

  const handleMethodReceiveMoneyChange = (e) => {
    const { value } = e.target;

    setRequest((prev) => ({
      ...prev,
      methodReceiveMoney: value,
      // ຜູ້ຮັບສິດ 1/2 ໃຊ້ສະເພາະກໍລະນີຈ່າຍໃຫ້ຜູ້ຮັບສິດ
      heir: String(value) === "1" ? prev.heir : "",
      // ເອກະສານມອບສິດໃຊ້ສະເພາະກໍລະນີຜູ້ຖືກມອບສິດ
      beneficiaryDocumentFile:
        String(value) === "2" ? prev.beneficiaryDocumentFile : null,
    }));
  };

  const handleDateChange = (date, field) => {
    setRequest((prev) => ({
      ...prev,
      [field]: date ? moment(date).format("YYYY-MM-DD") : "",
    }));
  };

  const handleImageSelect = (filePayload, key) => {
    const hasFiles =
      Array.isArray(filePayload?.files) && filePayload.files.length > 0;

    setRequest((prev) => ({
      ...prev,
      [key]: hasFiles ? { file: filePayload } : null,
    }));
  };

  const customer = contractDetail?.customer || {};
  const insuredName = [customer?.firstName, customer?.lastName]
    .filter(Boolean)
    .join(" ");
  const packageName =
    contractDetail?.packageName ||
    customer?.packageName ||
    contractDetail?.package?.name ||
    "-";

  const selectedCause = causeDeadList().find(
    (item) => String(item.value) === String(request.claimCause)
  );
  const selectedMethod = methodReceiveMoneyList().find(
    (item) => String(item.value) === String(request.methodReceiveMoney)
  );
  // ໂຫຼດລາຍຊື່ຜູ້ຮັບສິດຈາກ contractDetail ແຕ່ຮັກສາ value ເກົ່າ:
  // ຜູ້ຮັບສິດ 1 = "0", ຜູ້ຮັບສິດ 2 = "1"
  // fallback contractDetail.heir ເພື່ອຮອງຮັບ response shape ອີກແບບໜຶ່ງ.
  const contractHeirList = [
    contractDetail?.heirs1 || contractDetail?.heir?.[0],
    contractDetail?.heirs2 || contractDetail?.heir?.[1],
  ]
    .map((heir, index) => {
      const name = [heir?.firstName, heir?.lastName]
        .filter(Boolean)
        .join(" " )
        .trim();

      if (!name) return null;

      return {
        label: name,
        value: String(index),
        data: heir,
      };
    })
    .filter(Boolean);

  const selectedBank = bankList().find(
    (item) => String(item.value) === String(request.bankType)
  );
  const selectedHeir = contractHeirList.find(
    (item) => String(item.value) === String(request.heir)
  );
  const selectedReceiver =
    String(request.methodReceiveMoney) === "1"
      ? selectedHeir?.label
      : String(request.methodReceiveMoney) === "2"
        ? selectedMethod?.label
        : "";
  const receiveMoneySummary = [selectedBank?.label, selectedReceiver]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="page-wrapper dashboard life-claim-page">
      <span className="header-span"></span>
      <DashboardHeader />
      <MobileMenu />
      <DashboardEmployerSidebar />

      <section className="user-dashboard life-claim-dashboard">
        <div className="dashboard-outer">
          <div className="life-claim-topbar">
            <button
              type="button"
              className="life-claim-back"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left" />
              <span>{t("claimPages.common.back")}</span>
            </button>
          </div>

          <div className="life-claim-hero">
            <div className="life-claim-hero-copy">
              <div className="life-claim-hero-icon">
                <i className="fas fa-hand-holding-heart" />
              </div>
              <div>
                <span className="life-claim-eyebrow">{t("claimPages.life.eyebrow")}</span>
                <h3>{t("claimPages.life.title")}</h3>
                <p>{t("claimPages.life.subtitle")}</p>
              </div>
            </div>

            <div className="life-claim-flow">
              <FlowStep number="1" label={t("claimPages.life.steps.claim")} active />
              <FlowStep number="2" label={t("claimPages.life.steps.payment")} />
              <FlowStep number="3" label={t("claimPages.life.steps.documents")} />
              <FlowStep number="4" label={t("claimPages.life.steps.review")} />
            </div>
          </div>

          <form onSubmit={handleSave}>
            <div className="life-claim-layout">
              <main className="life-claim-main">
                <section className="life-claim-card life-contract-summary-card">
                  <SectionTitle
                    icon="fa-file-contract"
                    title={t("claimPages.life.contractTitle")}
                    subtitle={t("claimPages.life.contractSubtitle")}
                  />

                  {loadingContract ? (
                    <div className="life-claim-summary-loading">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : (
                    <div className="life-contract-summary-grid">
                      <SummaryItem
                        icon="fa-hashtag"
                        label={t("contracts.life.clame.contractNumber")}
                        value={request.contractNumber || "-"}
                        strong
                      />
                      <SummaryItem
                        icon="fa-user-shield"
                        label={t("claimPages.life.insured")}
                        value={insuredName || "-"}
                      />
                      <SummaryItem
                        icon="fa-phone"
                        label={t("claimPages.life.phone")}
                        value={customer?.phone || "-"}
                      />
                      <SummaryItem
                        icon="fa-box-open"
                        label={t("claimPages.life.package")}
                        value={packageName}
                      />
                    </div>
                  )}
                </section>

                <section className="life-claim-card">
                  <SectionTitle
                    icon="fa-notes-medical"
                    title={t("claimPages.life.detailTitle")}
                    subtitle={t("claimPages.life.detailSubtitle")}
                    badge={t("claimPages.common.step", { number: 1 })}
                  />

                  <div className="life-claim-form-grid">
                    <div className="life-claim-field">
                      <label>
                        {t("contracts.life.clame.claimCause")}
                        <Required />
                      </label>
                      <div className="life-claim-control-wrap life-claim-select-wrap">
                        <i className="fas fa-heartbeat" />
                        <select
                          className="life-claim-control with-icon"
                          name="claimCause"
                          onChange={handleChange}
                          value={request.claimCause}
                          required
                        >
                          <option value="">{t("pleaseSelect")}</option>
                          {causeDeadList().map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="life-claim-field">
                      <label>
                        {t("contracts.life.clame.deadDate")}
                        <Required />
                      </label>
                      <div className="life-claim-control-wrap life-claim-date-wrap">
                        <i className="fas fa-calendar-alt" />
                        <DatePicker
                          selected={
                            request.deadDate
                              ? moment(request.deadDate, "YYYY-MM-DD").toDate()
                              : null
                          }
                          onChange={(date) =>
                            handleDateChange(date, "deadDate")
                          }
                          dateFormat="dd/MM/yyyy"
                          placeholderText={t("contracts.life.clame.deadDate")}
                          className="life-claim-control with-icon"
                          maxDate={new Date()}
                          required
                        />
                      </div>
                    </div>

                    <div className="life-claim-field life-claim-field-full">
                      <label>{t("contracts.life.clame.description")}</label>
                      <textarea
                        className="life-claim-textarea"
                        name="description"
                        rows="4"
                        value={request.description}
                        onChange={handleChange}
                        placeholder={t("claimPages.life.detailPlaceholder")}
                      />
                    </div>
                  </div>
                </section>

                <section className="life-claim-card">
                  <SectionTitle
                    icon="fa-wallet"
                    title={t("claimPages.life.paymentTitle")}
                    subtitle={t("claimPages.life.paymentSubtitle")}
                    badge={t("claimPages.common.step", { number: 2 })}
                  />

                  <div className="life-claim-form-grid">
                    <div className="life-claim-field life-claim-field-full">
                      <label>
                        {t("contracts.life.clame.methodReceiveMoney")}
                        <Required />
                      </label>
                      <div className="life-claim-methods">
                        {methodReceiveMoneyList().map((item) => {
                          const selected =
                            String(request.methodReceiveMoney) ===
                            String(item.value);
                          return (
                            <label
                              key={item.value}
                              className={`life-claim-method ${
                                selected ? "selected" : ""
                              }`}
                            >
                              <input
                                type="radio"
                                name="methodReceiveMoney"
                                value={item.value}
                                checked={selected}
                                onChange={handleMethodReceiveMoneyChange}
                                required
                              />
                              <span className="life-claim-method-icon">
                                <i
                                  className={`fas ${
                                    String(item.value) === "1"
                                      ? "fa-user-check"
                                      : "fa-user-tie"
                                  }`}
                                />
                              </span>
                              <span className="life-claim-method-copy">
                                <strong>{item.label}</strong>
                                <small>
                                  {String(item.value) === "1"
                                    ? t("claimPages.life.beneficiaryPayment")
                                    : t("claimPages.life.agentPayment")}
                                </small>
                              </span>
                              <span className="life-claim-radio-dot" />
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {String(request.methodReceiveMoney) === "1" && (
                      <div className="life-claim-field life-claim-field-full life-claim-beneficiary-block">
                        <label>
                          {t("contracts.life.clame.heir")}
                          <Required />
                        </label>
                        <p className="life-claim-field-help">{t("claimPages.life.beneficiaryHelp")}</p>
                        <div className="life-claim-beneficiary-options">
                          {loadingContract ? (
                            <div className="life-claim-beneficiary-empty">
                              <i className="fas fa-spinner fa-spin" />
                              <span>{t("claimPages.life.beneficiaryLoading")}</span>
                            </div>
                          ) : contractHeirList.length === 0 ? (
                            <div className="life-claim-beneficiary-empty">
                              <i className="fas fa-user-slash" />
                              <span>{t("claimPages.life.beneficiaryEmpty")}</span>
                            </div>
                          ) : (
                            contractHeirList.map((item, index) => {
                            const selected =
                              String(request.heir) === String(item.value);
                            return (
                              <label
                                key={item.value}
                                className={`life-claim-beneficiary-option ${
                                  selected ? "selected" : ""
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="heir"
                                  value={item.value}
                                  checked={selected}
                                  onChange={handleChange}
                                  required
                                />
                                <span className="life-claim-beneficiary-number">
                                  {index + 1}
                                </span>
                                <span className="life-claim-beneficiary-copy">
                                  <strong>{item.label}</strong>
                                  <small>
                                    {t("claimPages.life.beneficiaryNo", { number: Number(item.value) + 1 })}
                                  </small>
                                </span>
                                <span className="life-claim-beneficiary-check">
                                  <i className="fas fa-check" />
                                </span>
                              </label>
                            );
                            })
                          )}
                        </div>
                      </div>
                    )}

                    <div className="life-claim-field">
                      <label>{t("contracts.life.clame.bankType")}</label>
                      <div className="life-claim-control-wrap life-claim-select-wrap">
                        <i className="fas fa-university" />
                        <select
                          className="life-claim-control with-icon"
                          name="bankType"
                          onChange={handleChange}
                          value={request.bankType}
                        >
                          <option value="">{t("pleaseSelect")}</option>
                          {bankList().map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="life-claim-field">
                      <label>{t("contracts.life.clame.bankAccName")}</label>
                      <div className="life-claim-control-wrap">
                        <i className="fas fa-id-card" />
                        <input
                          type="text"
                          name="bankAccName"
                          className="life-claim-control with-icon"
                          placeholder={t("contracts.life.clame.bankAccName")}
                          onChange={handleChange}
                          value={request.bankAccName}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="life-claim-field life-claim-field-full">
                      <label>{t("contracts.life.clame.bankAcc")}</label>
                      <div className="life-claim-control-wrap">
                        <i className="fas fa-credit-card" />
                        <input
                          type="text"
                          name="bankAcc"
                          className="life-claim-control with-icon"
                          placeholder={t("contracts.life.clame.bankAcc")}
                          onChange={handleChange}
                          value={request.bankAcc}
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>
                </section>

                <section className="life-claim-card" id="life-claim-documents">
                  <SectionTitle
                    icon="fa-paperclip"
                    title={t("claimPages.life.documentsTitle")}
                    subtitle={t("claimPages.life.documentsSubtitle")}
                    badge={t("claimPages.common.step", { number: 3 })}
                  />

                  <div
                    className="life-claim-upload-block"
                  >
                    <div className="life-claim-upload-heading">
                      <div>
                        <strong>{t("contracts.life.clame.document")}</strong>
                        <Required />
                        <p>{t("claimPages.life.documentsHelp")}</p>
                      </div>
                      <span className="life-claim-file-hint">JPG, PNG, PDF</span>
                    </div>
                    <FileUploadGrid
                      multiple={true}
                      folderName="clameDocumentFile"
                      onChange={(file) =>
                        handleImageSelect(file, "documentFile")
                      }
                      accept="image/*,application/pdf"
                      required
                      requiredMessage={t("claimPages.life.documentsRequired")}
                    />
                  </div>

                  {String(request.methodReceiveMoney) === "2" && (
                    <div
                      className="life-claim-upload-block secondary"
                    >
                      <div className="life-claim-upload-heading">
                        <div>
                          <strong>
                            {t("contracts.life.clame.beneficiaryDocument")}
                          </strong>
                          <Required />
                          <p>{t("claimPages.life.beneficiaryDocumentsHelp")}</p>
                        </div>
                        <span className="life-claim-file-hint">JPG, PNG, PDF</span>
                      </div>
                      <FileUploadGrid
                        multiple={true}
                        folderName="clameBeneficiaryDocument"
                        onChange={(file) =>
                          handleImageSelect(file, "beneficiaryDocumentFile")
                        }
                        accept="image/*,application/pdf"
                        required
                        requiredMessage={t("claimPages.life.beneficiaryDocumentsRequired")}
                      />
                    </div>
                  )}
                </section>

                <div className="life-claim-mobile-submit">
                  <button
                    className="life-claim-submit"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin" />
                        {t("claimPages.common.submitting")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane" />
                        {t("button.save")}
                      </>
                    )}
                  </button>
                </div>
              </main>

              <aside className="life-claim-side">
                <div className="life-claim-side-card sticky">
                  <div className="life-claim-side-title">
                    <span className="life-claim-side-icon">
                      <i className="fas fa-clipboard-check" />
                    </span>
                    <div>
                      <h5>{t("claimPages.life.summaryTitle")}</h5>
                      <p>{t("claimPages.life.summarySubtitle")}</p>
                    </div>
                  </div>

                  <div className="life-claim-review-list">
                    <ReviewRow
                      label={t("claimPages.life.contractNumber")}
                      value={request.contractNumber || "-"}
                    />
                    <ReviewRow
                      label={t("claimPages.life.claimCause")}
                      value={selectedCause?.label || t("claimPages.common.notSelected")}
                      empty={!selectedCause}
                    />
                    <ReviewRow
                      label={t("claimPages.life.deathDate")}
                      value={
                        request.deadDate
                          ? moment(request.deadDate).format("DD/MM/YYYY")
                          : t("claimPages.common.notSpecified")
                      }
                      empty={!request.deadDate}
                    />
                    <ReviewRow
                      label={t("claimPages.life.paymentMethod")}
                      value={receiveMoneySummary || t("claimPages.common.notSelected")}
                      empty={!receiveMoneySummary}
                    />
                  </div>

                  <div className="life-claim-security-note">
                    <i className="fas fa-shield-alt" />
                    <p>{t("claimPages.life.securityNote")}</p>
                  </div>

                  <button
                    className="life-claim-submit desktop"
                    type="submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin" />
                        {t("claimPages.common.submitting")}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane" />
                        {t("claimPages.common.submitRequest")}
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="life-claim-cancel"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  >
                    {t("claimPages.common.cancel")}
                  </button>
                </div>
              </aside>
            </div>
          </form>
        </div>
      </section>

      <CopyrightFooter />
    </div>
  );
};

const Required = () => <span className="life-claim-required">*</span>;

const FlowStep = ({ number, label, active = false }) => (
  <div className={`life-claim-flow-step ${active ? "active" : ""}`}>
    <span>{number}</span>
    <small>{label}</small>
  </div>
);

const SectionTitle = ({ icon, title, subtitle, badge }) => (
  <div className="life-claim-section-title">
    <div className="life-claim-section-icon">
      <i className={`fas ${icon}`} />
    </div>
    <div className="life-claim-section-copy">
      <div className="life-claim-section-heading">
        <h5>{title}</h5>
        {badge && <span>{badge}</span>}
      </div>
      <p>{subtitle}</p>
    </div>
  </div>
);

const SummaryItem = ({ icon, label, value, strong = false }) => (
  <div className="life-contract-summary-item">
    <span className="life-contract-summary-icon">
      <i className={`fas ${icon}`} />
    </span>
    <div>
      <small>{label}</small>
      <strong className={strong ? "primary" : ""}>{value}</strong>
    </div>
  </div>
);

const ReviewRow = ({ label, value, empty = false }) => (
  <div className="life-claim-review-row">
    <span>{label}</span>
    <strong className={empty ? "empty" : ""}>{value}</strong>
  </div>
);

export default LifeClaim;
